import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import DownloadIcon from '@mui/icons-material/Download';

const PLAYBACK_RATES = [0.75, 1, 1.25, 1.5, 2] as const;

interface AudioPlayerProps {
  src: string;
  title?: string;
  compact?: boolean;
  onEnded?: () => void;
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function AudioPlayer({
  src,
  title,
  compact,
  onEnded,
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const volumeAnchorRef = useRef<HTMLButtonElement>(null);

  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rateIndex, setRateIndex] = useState(1); // default 1x
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [volumeOpen, setVolumeOpen] = useState(false);

  const rate = PLAYBACK_RATES[rateIndex]!;

  // Sync audio element state
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration);
    const onDurationChange = () => setDuration(audio.duration);
    const onEnding = () => {
      setPlaying(false);
      onEnded?.();
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('ended', onEnding);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('ended', onEnding);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
    };
  }, [onEnded]);

  // Apply volume / muted changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = volume;
    audio.muted = muted;
  }, [volume, muted]);

  // Apply playback rate changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.playbackRate = rate;
  }, [rate]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      void audio.play();
    } else {
      audio.pause();
    }
  }, []);

  const handleSeek = useCallback((_: Event, value: number | number[]) => {
    const audio = audioRef.current;
    if (!audio) return;
    const t = typeof value === 'number' ? value : value[0]!;
    audio.currentTime = t;
    setCurrentTime(t);
  }, []);

  const cycleRate = useCallback(() => {
    setRateIndex((prev) => (prev + 1) % PLAYBACK_RATES.length);
  }, []);

  const handleVolumeChange = useCallback(
    (_: Event, value: number | number[]) => {
      const v = typeof value === 'number' ? value : value[0]!;
      setVolume(v);
      if (v > 0 && muted) setMuted(false);
    },
    [muted],
  );

  const toggleMute = useCallback(() => {
    setMuted((m) => !m);
  }, []);

  const VolumeIcon =
    muted || volume === 0
      ? VolumeOffIcon
      : volume < 0.5
        ? VolumeDownIcon
        : VolumeUpIcon;

  const height = compact ? 40 : 48;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Box>
      {/* Hidden native audio element */}
      <audio ref={audioRef} src={src} preload="metadata" />

      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          height,
          px: 1,
          borderRadius: 1,
          bgcolor: 'action.hover',
        }}
      >
        {/* Play / Pause */}
        <IconButton
          size="small"
          onClick={togglePlay}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {playing ? (
            <PauseIcon fontSize="small" />
          ) : (
            <PlayArrowIcon fontSize="small" />
          )}
        </IconButton>

        {/* Time display */}
        <Typography
          variant="caption"
          sx={{
            minWidth: compact ? 70 : 90,
            textAlign: 'center',
            userSelect: 'none',
          }}
        >
          {formatTime(currentTime)} / {formatTime(duration)}
        </Typography>

        {/* Progress / seek bar */}
        <Slider
          size="small"
          min={0}
          max={duration || 1}
          step={0.1}
          value={currentTime}
          onChange={handleSeek}
          aria-label="Seek"
          sx={{ flex: 1, mx: 1 }}
          // Show a tooltip with the formatted time on hover
          valueLabelDisplay="auto"
          valueLabelFormat={formatTime}
        />

        {/* Visible progress percentage for screen readers */}
        <Typography
          component="span"
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
          }}
        >
          {Math.round(progress)}% played
        </Typography>

        {/* Playback speed */}
        <IconButton
          size="small"
          onClick={cycleRate}
          aria-label={`Playback speed ${rate}x`}
          sx={{ fontSize: 12, width: 36, height: 28, borderRadius: 1 }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {rate}x
          </Typography>
        </IconButton>

        {/* Volume */}
        <IconButton
          ref={volumeAnchorRef}
          size="small"
          onClick={() => setVolumeOpen((o) => !o)}
          onDoubleClick={toggleMute}
          aria-label="Volume"
        >
          <VolumeIcon fontSize="small" />
        </IconButton>
        <Popper
          open={volumeOpen}
          anchorEl={volumeAnchorRef.current}
          placement="top"
        >
          <ClickAwayListener onClickAway={() => setVolumeOpen(false)}>
            <Paper
              sx={{ p: 1, height: 120, display: 'flex', alignItems: 'center' }}
            >
              <Slider
                orientation="vertical"
                size="small"
                min={0}
                max={1}
                step={0.05}
                value={muted ? 0 : volume}
                onChange={handleVolumeChange}
                aria-label="Volume"
                sx={{ height: 100 }}
              />
            </Paper>
          </ClickAwayListener>
        </Popper>

        {/* Download */}
        {!compact && (
          <IconButton
            size="small"
            component="a"
            href={src}
            download={title ?? true}
            aria-label="Download"
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      {/* Title (only in non-compact mode) */}
      {title && !compact && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: 'block' }}
        >
          {title}
        </Typography>
      )}
    </Box>
  );
}
